import React, { useState, useEffect, useCallback } from 'react';
import { api } from '@/lib/api';
import { logger } from '@/lib/logger';
import {
  Spinner, ShieldCheck, Sparkle, Plus, Trash, Check, Baby, EyeSlash,
} from '@phosphor-icons/react';

const Toggle = ({ on, onChange, testid }) => (
  <button onClick={() => onChange(!on)} data-testid={testid} role="switch" aria-checked={on}
    className={`relative w-10 h-5.5 h-6 rounded-full transition-colors ${on ? 'bg-[#002FA7]' : 'bg-[#D1D5DB]'}`}>
    <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${on ? 'translate-x-4' : ''}`} />
  </button>
);

export const PrivacyTab = ({ caseId }) => {
  const [config, setConfig] = useState({ enabled: false, child_sensitive: false, aliases: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/cases/${caseId}/redaction`);
      setConfig({ enabled: false, child_sensitive: false, aliases: [], ...(data.redaction || {}) });
    } catch (err) {
      logger.error('failed to load redaction config:', err);
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    setSaved(false);
    try {
      const { data } = await api.put(`/cases/${caseId}/redaction`, config);
      setConfig(c => ({ ...c, ...(data.redaction || {}) }));
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      logger.error('failed to save redaction config:', err);
      alert('Failed to save privacy settings.');
    } finally {
      setSaving(false);
    }
  };

  const suggest = async () => {
    setSuggesting(true);
    try {
      const { data } = await api.post(`/cases/${caseId}/redaction/suggest`);
      const existing = new Set(config.aliases.map(a => a.real_name.toLowerCase()));
      const merged = [
        ...config.aliases,
        ...(data.suggestions || []).filter(s => !existing.has(s.real_name.toLowerCase()))
          .map(s => ({ real_name: s.real_name, alias: s.alias, is_minor: !!s.is_minor })),
      ];
      setConfig(c => ({ ...c, aliases: merged }));
    } catch (err) {
      logger.error('failed to suggest aliases:', err);
      alert(err?.response?.data?.detail || 'Failed to suggest aliases. Analyze documents first.');
    } finally {
      setSuggesting(false);
    }
  };

  const setAlias = (idx, field, value) => {
    setConfig(c => ({
      ...c,
      aliases: c.aliases.map((a, i) => (i === idx ? { ...a, [field]: value } : a)),
    }));
  };

  const removeAlias = (idx) => setConfig(c => ({ ...c, aliases: c.aliases.filter((_, i) => i !== idx) }));
  const addAlias = () => setConfig(c => ({ ...c, aliases: [...c.aliases, { real_name: '', alias: '', is_minor: false }] }));

  if (loading) {
    return <div className="flex items-center justify-center py-20" data-testid="privacy-tab"><Spinner size={28} className="animate-spin text-[#002FA7]" /></div>;
  }

  return (
    <div data-testid="privacy-tab" className="space-y-5 max-w-3xl">
      <div>
        <h3 className="font-outfit text-lg font-semibold text-[#09090B] flex items-center gap-2">
          <ShieldCheck size={20} className="text-[#002FA7]" weight="fill" /> Privacy — Redaction &amp; Aliases
        </h3>
        <p className="text-sm text-[#6B7280] font-ibm mt-0.5">
          Replace real names with neutral aliases in every export (chronology, reports). Child-sensitive mode always protects minors, even when full redaction is off.
        </p>
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-lg divide-y divide-[#E5E7EB]">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-start gap-3">
            <EyeSlash size={18} className="text-[#002FA7] mt-0.5" weight="fill" />
            <div>
              <p className="text-sm font-ibm font-semibold text-[#09090B]">Redact exports</p>
              <p className="text-xs text-[#6B7280] font-ibm">All aliased names below are replaced in exported files.</p>
            </div>
          </div>
          <Toggle on={config.enabled} onChange={v => setConfig(c => ({ ...c, enabled: v }))} testid="redaction-enabled-toggle" />
        </div>
        <div className="flex items-center justify-between p-4">
          <div className="flex items-start gap-3">
            <Baby size={18} className="text-[#002FA7] mt-0.5" weight="fill" />
            <div>
              <p className="text-sm font-ibm font-semibold text-[#09090B]">Child-sensitive mode</p>
              <p className="text-xs text-[#6B7280] font-ibm">Names marked as minors are ALWAYS redacted in exports — even when full redaction is off.</p>
            </div>
          </div>
          <Toggle on={config.child_sensitive} onChange={v => setConfig(c => ({ ...c, child_sensitive: v }))} testid="child-sensitive-toggle" />
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h4 className="font-ibm text-xs font-bold uppercase tracking-wider text-[#6B7280]">Alias map ({config.aliases.length})</h4>
        <div className="flex items-center gap-2">
          <button onClick={addAlias} data-testid="alias-add-row"
            className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-[#E5E7EB] text-[#09090B] font-ibm font-semibold rounded-md text-xs hover:bg-[#F3F4F6] transition-all">
            <Plus size={12} /> Add name
          </button>
          <button onClick={suggest} disabled={suggesting} data-testid="alias-suggest-button"
            className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-[#002FA7]/30 text-[#002FA7] font-ibm font-semibold rounded-md text-xs hover:bg-[#002FA7]/5 disabled:opacity-50 transition-all">
            {suggesting ? <><Spinner size={12} className="animate-spin" /> Suggesting…</> : <><Sparkle size={12} weight="fill" /> Suggest aliases (AI)</>}
          </button>
        </div>
      </div>

      {config.aliases.length === 0 ? (
        <div className="text-center py-10 bg-white border border-dashed border-[#E5E7EB] rounded-lg" data-testid="alias-empty">
          <p className="text-sm text-[#6B7280] font-ibm">No aliases yet. Use <span className="font-semibold text-[#002FA7]">Suggest aliases (AI)</span> to build the map from people found in your documents, or add names manually.</p>
        </div>
      ) : (
        <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden" data-testid="alias-table">
          <div className="grid grid-cols-[1fr_1fr_70px_40px] gap-2 px-4 py-2 bg-[#F9FAFB] border-b border-[#E5E7EB] text-[10px] font-ibm font-bold uppercase tracking-wider text-[#6B7280]">
            <span>Real name</span><span>Alias</span><span>Minor</span><span></span>
          </div>
          {config.aliases.map((a, i) => (
            <div key={`alias-${i}`} className="grid grid-cols-[1fr_1fr_70px_40px] gap-2 px-4 py-2 items-center border-b border-[#F3F4F6] last:border-0" data-testid={`alias-row-${i}`}>
              <input value={a.real_name} onChange={e => setAlias(i, 'real_name', e.target.value)} placeholder="Jane Doe"
                data-testid={`alias-real-${i}`}
                className="text-sm font-ibm border border-[#E5E7EB] rounded-md px-2 py-1 focus:outline-none focus:border-[#002FA7]" />
              <input value={a.alias} onChange={e => setAlias(i, 'alias', e.target.value)} placeholder="Parent A"
                data-testid={`alias-alias-${i}`}
                className="text-sm font-ibm border border-[#E5E7EB] rounded-md px-2 py-1 focus:outline-none focus:border-[#002FA7]" />
              <label className="flex items-center justify-center cursor-pointer">
                <input type="checkbox" checked={!!a.is_minor} onChange={e => setAlias(i, 'is_minor', e.target.checked)}
                  data-testid={`alias-minor-${i}`} className="w-4 h-4 accent-[#002FA7]" />
              </label>
              <button onClick={() => removeAlias(i)} data-testid={`alias-delete-${i}`}
                className="text-[#6B7280] hover:text-red-600 flex justify-center"><Trash size={14} /></button>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-3">
        <button onClick={save} disabled={saving} data-testid="redaction-save-button"
          className="flex items-center gap-1.5 px-4 py-2 bg-[#002FA7] text-white font-ibm font-semibold rounded-md text-sm hover:bg-[#00237D] disabled:opacity-50 transition-all">
          {saving ? <><Spinner size={14} className="animate-spin" /> Saving…</> : <><Check size={14} /> Save privacy settings</>}
        </button>
        {saved && <span className="text-sm font-ibm text-green-700 flex items-center gap-1" data-testid="redaction-saved-indicator"><Check size={14} /> Saved</span>}
      </div>
    </div>
  );
};
